import React, { useState } from 'react';
import { Sparkles, User, Wrench, ChevronDown, ChevronRight } from 'lucide-react';
import type { WorkbenchMessage, MessageContentBlock, ToolUseBlock, ToolResultBlock } from '../../../types';
import {
  ProfileDetailCard,
  DictionaryDetailCard,
  RulesDetailCard,
  MappingDetailCard,
} from './DetailCards';

// Tools that have a rich detail card replacing their raw JSON tool_result
const DETAIL_CARD_TOOLS = new Set([
  'profile_tables',
  'generate_dictionary',
  'suggest_rules',
  'suggest_mapping',
]);

interface Props {
  message: WorkbenchMessage;
  artifacts?: Record<string, any>;
  // IDs of tool_use blocks that have a detail card rendered — their raw
  // tool_result should be suppressed to avoid duplicate/redundant display.
  detailCardToolUseIds?: Set<string>;
}

const MessageBubble: React.FC<Props> = ({ message, artifacts, detailCardToolUseIds }) => {
  const isUser = message.role === 'user';
  const content = message.content;

  // Tool-result wrapper messages (role=user, content=[tool_result,...]) are noise
  // for the user — show a compact indicator instead of a full bubble.
  if (
    !isUser
      ? false
      : Array.isArray(content) && content.every((b: any) => b?.type === 'tool_result')
  ) {
    // Hide tool_results whose matching tool_use already has a detail card
    const visible = (content as ToolResultBlock[]).filter(
      (b) => !detailCardToolUseIds?.has(b.tool_use_id),
    );
    if (visible.length === 0) return null;
    return <ToolResultsStrip blocks={visible} />;
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-cargo-accent/20' : 'bg-cargo-accent text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4 text-cargo-accent" /> : <Sparkles className="w-4 h-4" />}
      </div>

      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className="text-cargo-muted text-[11px] uppercase tracking-wider mb-1">
          {isUser ? 'You' : 'Incident Analyst'}
          {message.at && (
            <span className="ml-2 normal-case tracking-normal opacity-70">
              {new Date(message.at).toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Content */}
        {typeof content === 'string' ? (
          <div
            className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              isUser
                ? 'bg-cargo-accent text-white'
                : 'bg-cargo-panel border border-cargo-border text-cargo-text'
            }`}
          >
            {content}
          </div>
        ) : (
          <div className="space-y-2">
            {(content as MessageContentBlock[]).map((block, i) => (
              // Wrap each block in a div so inline-block bubbles don't float
              // next to each other on the same line — each block stacks below
              // the previous one in arrival order.
              <div key={i}>
                <Block block={block} isUser={isUser} />
                {/* Inline detail card after a known tool call */}
                {block.type === 'tool_use' && (
                  <DetailAfterToolUse block={block as ToolUseBlock} artifacts={artifacts} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Block: React.FC<{ block: MessageContentBlock; isUser: boolean }> = ({ block, isUser }) => {
  if (block.type === 'text') {
    return (
      <div
        className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-cargo-accent text-white'
            : 'bg-cargo-panel border border-cargo-border text-cargo-text'
        }`}
      >
        {renderTextWithLinks(block.text || '', isUser)}
      </div>
    );
  }
  if (block.type === 'tool_use') {
    return <ToolUseCard block={block} />;
  }
  if (block.type === 'tool_result') {
    return <ToolResultCard block={block} />;
  }
  return null;
};

const ToolUseCard: React.FC<{ block: ToolUseBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-accent" />
        <span className="font-mono">{block.name}</span>
        <span className="text-cargo-muted opacity-60">called</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-surface/80 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-40">
          {JSON.stringify(block.input, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ToolResultCard: React.FC<{ block: ToolResultBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-success" />
        <span className="text-cargo-muted opacity-80">tool result</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-surface/80 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-60">
          {safePretty(block.content)}
        </pre>
      )}
    </div>
  );
};

const ToolResultsStrip: React.FC<{ blocks: ToolResultBlock[] }> = ({ blocks }) => (
  <div className="flex gap-3 justify-start">
    <div className="w-8 flex-shrink-0" />
    <div className="flex gap-1 flex-wrap">
      {blocks.map((b, i) => (
        <ToolResultCard key={i} block={b} />
      ))}
    </div>
  </div>
);

function safePretty(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/**
 * Render agent text with inline markdown-link + bare-URL support.
 *
 * Recognises two shapes in order:
 *   1. Markdown: `[label](url)` — rendered as `<a href={url}>label</a>`
 *   2. Bare paths starting with `/` (e.g. `/mapping/runs/abc`) or
 *      absolute http(s) URLs — rendered as `<a>` with the URL as both
 *      label and href.
 *
 * Keeps everything else as plain text. Preserves whitespace (the parent
 * div has `whitespace-pre-wrap`). Intentionally minimal — no bold, no
 * headers, no lists — the agent's chat replies are usually short and
 * this keeps the renderer dependency-free.
 */
function renderTextWithLinks(text: string, isUser: boolean): React.ReactNode[] {
  const MD_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;          // [label](url)
  // Bare URL — either absolute http(s), OR a relative path with at least
  // TWO segments (e.g. `/mapping/runs/xyz`). The 2-segment rule prevents
  // agent prose like "financial/loyalty" or "(yes/no)" from being turned
  // into fake hyperlinks.
  const BARE_URL = /(https?:\/\/[^\s)]+|\/[\w\-]+\/[\w/\-?&=.#%]+)/g;

  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;

  const linkClass = isUser
    ? 'underline decoration-white/60 hover:decoration-white'
    : 'text-cargo-accent hover:underline';

  // First pass: extract markdown links.
  const seen: Array<{ start: number; end: number; href: string; label: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = MD_LINK.exec(text)) !== null) {
    seen.push({ start: m.index, end: m.index + m[0].length, href: m[2], label: m[1] });
  }
  // Second pass: find bare URLs in regions NOT covered by markdown links.
  BARE_URL.lastIndex = 0;
  while ((m = BARE_URL.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const overlaps = seen.some((s) => !(end <= s.start || start >= s.end));
    if (overlaps) continue;
    seen.push({ start, end, href: m[0], label: m[0] });
  }
  seen.sort((a, b) => a.start - b.start);

  for (const link of seen) {
    if (link.start > lastIdx) {
      parts.push(text.slice(lastIdx, link.start));
    }
    const isExternal = /^https?:/i.test(link.href);
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={link.href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className={linkClass}
      >
        {link.label}
      </a>
    );
    lastIdx = link.end;
  }
  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }
  return parts.length ? parts : [text];
}

/**
 * Renders a rich detail card next to a tool_use block when the tool name
 * matches one of the known artifact-producing tools. Reads from session
 * artifacts (passed from ConversationPage) — no extra fetch for profile,
 * dictionary, rules. The mapping card lazily fetches its run detail.
 */
const DetailAfterToolUse: React.FC<{
  block: ToolUseBlock;
  artifacts?: Record<string, any>;
}> = ({ block, artifacts }) => {
  if (!artifacts) return null;

  const toolName = block.name;

  if (toolName === 'profile_tables' && artifacts.profile) {
    return (
      <div className="mt-1.5">
        <ProfileDetailCard profile={artifacts.profile} />
      </div>
    );
  }

  if (toolName === 'generate_dictionary' && artifacts.dictionary) {
    return (
      <div className="mt-1.5">
        <DictionaryDetailCard dictionary={artifacts.dictionary} />
      </div>
    );
  }

  if (toolName === 'suggest_rules' && Array.isArray(artifacts.rules)) {
    return (
      <div className="mt-1.5">
        <RulesDetailCard rules={artifacts.rules} />
      </div>
    );
  }

  if (toolName === 'suggest_mapping' && Array.isArray(artifacts.mapping_runs)) {
    // Find the latest mapping run (session.artifacts appends each new one)
    const runs = artifacts.mapping_runs as Array<{ run_id?: string }>;
    const latest = runs.length ? runs[runs.length - 1] : null;
    if (latest?.run_id) {
      return (
        <div className="mt-1.5">
          <MappingDetailCard runId={latest.run_id} />
        </div>
      );
    }
  }

  return null;
};

export default MessageBubble;
